import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { clearAuth, getStoredAuth } from '../../lib/auth';

const navLinks = [
  { to: '/articles', label: 'Articles' },
  // { to: '/issues', label: 'Issues' },
  { to: '/editorial-board', label: 'Editorial Board' },
  { to: '/author-guidelines', label: 'Author Guidelines' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [auth, setAuth] = useState(() => getStoredAuth());
  const [profileOpen, setProfileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const syncAuth = () => setAuth(getStoredAuth());
    window.addEventListener('storage', syncAuth);
    window.addEventListener('jamsd-auth-change', syncAuth);
    return () => {
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('jamsd-auth-change', syncAuth);
    };
  }, []);

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => setMenuOpen(false);
  const canSubmit = auth?.user?.role === 'author';
  const handleLogout = () => setLogoutOpen(true);
  const confirmLogout = () => {
    clearAuth();
    setLogoutOpen(false);
    setProfileOpen(false);
    closeMenu();
    navigate('/');
  };

  const activeClass = 'text-amber border-b-2 border-amber pb-0.5';
  const inactiveClass = 'text-slate-pale hover:text-white transition-colors';

  return (
    <>
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-navy transition-shadow ${
        scrolled ? 'shadow-lg' : ''
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo / Brand */}
          <Link
            to="/"
            onClick={closeMenu}
            className="flex items-center gap-2.5 flex-shrink-0 min-w-0"
          >
            <img src="/logo.svg" alt="JAMSD logo" className="h-10 w-10 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="font-display text-white text-xs sm:text-sm leading-tight hidden sm:block max-w-xs truncate">
                Journal of Applied Medical and Surgical Disciplines
              </span>
              <span className="font-display text-white text-sm sm:hidden leading-tight">
                JAMSD
              </span>
              <span className="font-sans font-bold text-amber text-base sm:text-lg leading-tight tracking-wide">
                JAMSD
              </span>
            </div>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6 mx-4">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `font-sans text-sm font-medium whitespace-nowrap ${isActive ? activeClass : inactiveClass}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          {/* Desktop CTA buttons */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            {canSubmit && (
              <Link
                to="/submit"
                className="btn-amber text-sm py-2 px-4 whitespace-nowrap"
              >
                Submit Manuscript
              </Link>
            )}
            {auth?.user ? (
  <div className="relative" ref={profileRef}>
    
    {/* Profile Button */}
    <button
      type="button"
      onClick={() => setProfileOpen((prev) => !prev)}
      className="group relative flex items-center justify-center"
    >
      <div className="w-11 h-11 rounded-full bg-amber text-navy font-bold flex items-center justify-center text-sm uppercase border-2 border-white shadow-md transition-transform group-hover:scale-105">
        {auth.user.fullName?.charAt(0) || 'U'}
      </div>

      {/* Hover Name Tooltip */}
      <div className="absolute top-14 right-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200">
        <div className="bg-white text-navy text-sm font-medium px-3 py-2 rounded-lg shadow-lg whitespace-nowrap border border-bg-border">
          {auth.user.fullName}
        </div>
      </div>
    </button>

    {/* Dropdown */}
    {profileOpen && (
      <div className="absolute right-0 mt-3 w-72 rounded-2xl bg-white shadow-2xl border border-bg-border overflow-hidden z-50 animate-fadeIn">
        
        {/* User Header */}
        <div className="bg-bg-ice px-5 py-5 border-b border-bg-border">
          <div className="flex items-center gap-4">
            
            <div className="w-14 h-14 rounded-full bg-amber text-navy font-bold flex items-center justify-center text-xl">
              {auth.user.fullName?.charAt(0) || 'U'}
            </div>

            <div className="min-w-0">
              <h3 className="font-sans text-lg font-bold text-navy truncate">
                {auth.user.fullName}
              </h3>

              <p className="text-sm text-slate capitalize">
                {auth.user.role}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-3 space-y-2">
          
          <Link
            to={['editor', 'admin', 'superadmin'].includes(auth.user.role) ? '/admin' : '/dashboard'}
            onClick={() => setProfileOpen(false)}
            className="flex items-center rounded-xl px-4 py-3 text-sm font-semibold text-navy hover:bg-bg-ice transition-colors"
          >
            Dashboard
          </Link>

          <button
            type="button"
            onClick={handleLogout}
            className="w-full text-left rounded-xl px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    )}
  </div>
) : (
              <Link
                to="/auth/login"
                className="btn-amber text-sm py-2 px-4 whitespace-nowrap"
              >
                Submit Manuscript
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={toggleMenu}
            className="lg:hidden inline-flex items-center justify-center p-2 rounded text-slate-light hover:text-white hover:bg-navy-medium transition-colors focus:outline-none focus:ring-2 focus:ring-amber"
            aria-label="Toggle navigation menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile slide-down menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            menuOpen ? 'max-h-screen opacity-100 pb-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="border-t border-navy-border pt-4 space-y-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `block font-sans text-sm font-medium px-3 py-2 rounded transition-colors ${
                    isActive
                      ? 'text-amber bg-navy-medium'
                      : 'text-slate-pale hover:text-white hover:bg-navy-medium'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-navy-border mt-3 flex flex-col gap-2 px-1">
              {canSubmit && (
                <Link
                  to="/submit"
                  onClick={closeMenu}
                  className="btn-amber text-sm py-2 text-center"
                >
                  Submit Manuscript
                </Link>
              )}
              {auth?.user ? (
                <>
                  <Link
                    to={['editor', 'admin', 'superadmin'].includes(auth.user.role) ? '/admin' : '/dashboard'}
                    onClick={closeMenu}
                    className="btn-ghost text-sm py-2 text-center border border-navy-border"
                  >
                    Dashboard
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="btn-amber text-sm py-2 px-4 whitespace-nowrap"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/auth/login"
                  onClick={closeMenu}
                  className="btn-amber text-sm py-2 px-4 whitespace-nowrap"
                >
                  Submit Manuscript
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
    {logoutOpen && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
          <h2 className="font-display text-xl font-bold text-navy">Sign Out</h2>
          <p className="mt-2 font-sans text-sm text-slate">Are you sure you want to sign out?</p>
          <div className="mt-5 flex justify-end gap-3">
            <button type="button" onClick={() => setLogoutOpen(false)} className="rounded border border-bg-border px-4 py-2 text-sm font-semibold text-navy">
              Cancel
            </button>
            <button type="button" onClick={confirmLogout} className="rounded bg-red-600 px-4 py-2 text-sm font-semibold text-white">
              Sign Out
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export default Navbar;
